import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { uploadImage } from '../../api/media'; // Ajusta la ruta si es necesario
import toast from 'react-hot-toast';
import { FaCamera, FaSpinner, FaTimes } from 'react-icons/fa';

const ImageUploader = ({ onChange, initialImageUrl = null, label = "Imagen", uploader, multiple = false }) => {
  const toArray = (value) => (Array.isArray(value) ? value : value ? [value] : []);
  const resolvePreviewUrl = (value) => {
    if (!value) return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'object') return value.url || value.path || value.secure_url || '';
    return '';
  };

  const initialValues = toArray(initialImageUrl);
  const [values, setValues] = useState(initialValues);
  const [previews, setPreviews] = useState(initialValues.map(resolvePreviewUrl).filter(Boolean));
  const [isUploading, setIsUploading] = useState(false);

  const onDrop = useCallback(async (acceptedFiles) => {
    const validFiles = (acceptedFiles || []).filter((file) => file instanceof File && file.size > 0);
    if (validFiles.length === 0) {
      toast.error('No se detecto una imagen valida.');
      return;
    }

    // Crear una vista previa local inmediatamente
    const newPreviews = validFiles.map(file => URL.createObjectURL(file));
    setPreviews(prev => (multiple ? [...prev, ...newPreviews] : [newPreviews[0]]));

    // Empezar la subida
    setIsUploading(true);
    try {
      // 🟢 2. Subimos todos los archivos en paralelo
      const uploadPromises = validFiles.map(file => uploader(file));
      const uploadedMedia = await Promise.all(uploadPromises);

      if (uploadedMedia.length > 0) {
        const nextValues = multiple ? [...values, ...uploadedMedia] : [uploadedMedia[0]];
        setValues(nextValues);
        setPreviews(nextValues.map(resolvePreviewUrl).filter(Boolean));

        if (multiple) {
          onChange(nextValues);
        } else {
          onChange(nextValues[0]);
        }

        toast.success(`${uploadedMedia.length} imagen(es) subida(s) con éxito.`);
      } else {
        setValues(values);
        setPreviews(values.map(resolvePreviewUrl).filter(Boolean));
      }
    } catch (error) {
      console.error("Error en onDrop de ImageUploader:", error);
      setValues(values);
      setPreviews(values.map(resolvePreviewUrl).filter(Boolean));
    } finally {
      setIsUploading(false);
    }
  }, [onChange, uploader, multiple, values]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/*': ['.jpeg', '.jpg', '.png', '.gif'] },
    multiple: multiple, // 🟢 4. Usamos la prop 'multiple'
  });

  const removeImage = (e, index) => {
    e.stopPropagation(); // Evitar que se abra el diálogo de archivo
    const nextValues = values.filter((_, i) => i !== index);
    const nextPreviews = previews.filter((_, i) => i !== index);
    setValues(nextValues);
    setPreviews(nextPreviews);
    onChange(multiple ? nextValues : nextValues[0] || null);
  };

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
        {label}
      </label>
      <div
        {...getRootProps()}
        className={`relative w-full h-48 border-2 border-dashed rounded-lg flex items-center justify-center text-center cursor-pointer
                   transition-colors duration-200 ${isDragActive ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'border-gray-300 dark:border-gray-600 hover:border-blue-400'}`}
      >
        <input {...getInputProps()} />

        {isUploading ? (
          <div className="flex flex-col items-center text-gray-500">
            <FaSpinner className="animate-spin text-3xl" />
            <p className="mt-2">Subiendo...</p>
          </div>
        ) : previews.length > 0 ? (
          <>
            {previews.map((preview, index) => (
              <div key={index} className="relative w-28 h-28">
                <img src={preview} alt="Vista previa" className="w-full h-full object-cover rounded-lg" />
                <button
                  onClick={(e) => removeImage(e, index)}
                  className="absolute top-1 right-1 bg-black bg-opacity-60 text-white rounded-full p-1 hover:bg-red-500"
                  aria-label="Eliminar imagen"
                >
                  <FaTimes size={12} />
                </button>
              </div>
            ))}
          </>
        ) : (
          <div className="absolute flex flex-col items-center text-gray-500">
            <FaCamera className="text-3xl" />
            <p className="mt-2 text-sm">
              {isDragActive ? 'Suelta la imagen aquí' : 'Arrastra una imagen o haz clic para seleccionar'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ImageUploader;